import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  OnInit,
  Signal,
  signal,
  untracked,
  ViewChild,
  WritableSignal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { InputMaskDirective } from '@iot-platform/directives';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged, filter, tap } from 'rxjs/operators';
import { AbstractFormFieldComponent } from '../../abstract-form-field.component';
import { AutocompleteFormField, AutocompleteOptionGroup, FormField } from '../../form-field.model';

@Component({
  selector: 'iot-platform-ui-form-autocomplete-field',
  imports: [
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatOptionModule,
    MatSelectModule,
    ReactiveFormsModule,
    TranslatePipe,
    MatTooltipModule,
    MatButtonModule,
    MatAutocompleteModule,
    MatProgressSpinnerModule,
    MatCheckbox,
    InputMaskDirective,
    FormsModule,
    NgClass,
    NgTemplateOutlet
  ],
  templateUrl: './form-autocomplete-field.component.html',
  styleUrl: '../../form-field/form-field.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FormAutocompleteFieldComponent extends AbstractFormFieldComponent<AutocompleteFormField> implements OnInit {
  private readonly translateService: TranslateService = inject(TranslateService);
  @ViewChild('inputAutoComplete') inputAutoComplete!: ElementRef;
  controlStatus: WritableSignal<string> = signal('VALID');
  autocompleteTooltipDisabled: WritableSignal<boolean> = signal(true);
  autocompleteFilteredOptions: WritableSignal<unknown[]> = signal([]);
  autocompleteFilteredOptionGroups: WritableSignal<AutocompleteOptionGroup<unknown>[]> = signal([]);
  autocompleteSelectedItems: Signal<unknown[]> = computed(() =>
    (this.autocompleteFilteredOptions() as { selected?: boolean }[]).filter((item) => item.selected)
  );
  displayByFn!: (item: unknown) => string;

  checkAutocompleteTooltip(element: HTMLElement): void {
    this.autocompleteTooltipDisabled.set(element.scrollWidth <= element.clientWidth);
  }

  ngOnInit() {
    super.ngOnInit();
    const control = this.control();
    const field = this.field();
    if (control && field) {
      this.displayByFn = this.autoCompleteDisplayWrapper.bind(this);
      this.onValueChangesInit();
    }
  }

  override onValueChangesInit(): void {
    const control = this.control();
    const field = this.field();
    if (control && field) {
      control?.valueChanges
        ?.pipe(
          filter((term) => typeof term === 'string'),
          debounceTime(field?.debounceTime?.() ?? this.defaultDebounceTime),
          tap((term: string) => {
            if (!term?.length || (field?.minLength && term.length < field.minLength())) {
              this.autocompleteFilteredOptions.set([]);
              this.autocompleteFilteredOptionGroups.set([]);
            }
          }),
          filter((term: string) => {
            if (field?.minLength) {
              return term?.length >= field.minLength();
            }
            return true;
          }),
          tap((value) => field?.valueChange?.(value)),
          distinctUntilChanged(),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe((value) => {
          this.filterAutocomplete(value);
        });

      control.statusChanges?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((status) => this.controlStatus.set(status));
      this.initAutoCompleteItemsEffects();
    }
  }

  override initInitialValueEffect() {
    super.initInitialValueEffect();
    effect(
      () => {
        const field = this.field();
        if (field?.initialValue) {
          const initialValue = field.initialValue();
          if (initialValue !== undefined) {
            (field as unknown as FormField)?.selectionChange?.({ option: { value: initialValue } });
          }
        }
      },
      { injector: this.injector }
    );
  }

  toggleAutoCompleteSelection(item: { selected?: boolean }): void {
    item.selected = !item.selected;
    const field = this.field();
    const autocompleteFilteredOptions = this.autocompleteFilteredOptions() as { selected?: boolean }[];
    const index = autocompleteFilteredOptions.findIndex((option) => option === item);
    if (index !== -1) {
      autocompleteFilteredOptions[index].selected = item.selected;
    }
    this.autocompleteFilteredOptions.set([...autocompleteFilteredOptions]);
    field?.selectionChange?.({ option: { value: item, values: autocompleteFilteredOptions.filter((elem) => elem.selected) } });
  }

  filterAutocomplete(searchTerm: unknown): void {
    const field = this.field();
    const value = searchTerm !== null && searchTerm !== undefined ? searchTerm : '';
    const searchStr = typeof value === 'string' ? value : JSON.stringify(value);

    const optionGroups = field?.optionGroups?.();
    if (optionGroups?.length) {
      const filteredGroups = optionGroups
        .map((group) => ({
          ...group,
          items: group.items.filter((item) => {
            const displayedValue = this.displayByFn(item);
            const translatedValue = displayedValue ? this.translateService.instant(displayedValue) : '';
            return JSON.stringify(translatedValue)?.toLowerCase().indexOf(searchStr?.toLowerCase()) !== -1;
          })
        }))
        .filter((group) => group.items.length > 0);
      this.autocompleteFilteredOptionGroups.set(filteredGroups);
      this.autocompleteFilteredOptions.set([]);
    } else {
      const values = (field?.items?.() as unknown[])?.filter((item) => {
        const displayedValue = this.displayByFn(item);
        const translatedValue = displayedValue ? this.translateService.instant(displayedValue) : '';
        return JSON.stringify(translatedValue)?.toLowerCase().indexOf(searchStr?.toLowerCase()) !== -1;
      });
      this.autocompleteFilteredOptions.set(values ?? []);
      field?.onFilteredItems?.(values ?? []);
      this.autocompleteFilteredOptionGroups.set([]);
    }
  }

  onFilterInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    const field = this.field();
    if (field?.minLength && value.length < field.minLength()) {
      this.autocompleteFilteredOptions.set([]);
      this.autocompleteFilteredOptionGroups.set([]);
      return;
    }
    if (field?.requireSelection?.()) {
      this.control()?.markAsTouched();
      this.control()?.setErrors({ requireSelection: true });
    }
    this.filterAutocomplete(value);
  }

  clearAutocomplete($event?: Event): void {
    $event?.stopPropagation?.();
    this.control()?.reset?.();
    this.inputAutoComplete?.nativeElement?.focus();
    this.field()?.onReset?.();
  }

  onAutoCompleteOptionsSelected(event: { option: { value: unknown } }): void {
    const field = this.field();
    const control = this.control();
    control?.setValue(event?.option?.value);
    if (!field?.multiple?.()) {
      // For multi select the event will be triggered in this method toggleAutoCompleteSelection
      field?.selectionChange?.(event);
    }
    if (field?.clearOnSelect?.()) {
      this.clearAutocomplete();
    }
  }

  private initAutoCompleteItemsEffects() {
    effect(
      () => {
        this.field()?.items?.();
        this.field()?.optionGroups?.();
        untracked(() => {
          this.filterAutocomplete(null);
        });
      },
      { injector: this.injector }
    );
  }

  private autoCompleteDisplayWrapper(item: unknown) {
    if (typeof item === 'string') return item;
    return this.field()?.displayBy?.(item) as string;
  }
}
